import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout as authLogout } from '../services/authService';
import { fetchRecentAlarms, fetchNotices, getFullImageUrl } from '../services/api';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useSystemSettings } from '../context/SystemSettingsContext';
import './Header.css';

// XSS 방지: 이미지 URL 검증
const sanitizeImageUrl = (url) => {
  if (!url) return '';

  // 허용된 프로토콜만 허용
  const allowedProtocols = ['http:', 'https:', 'data:'];

  try {
    // data: URL인 경우 이미지 타입만 허용
    if (url.startsWith('data:')) {
      if (url.startsWith('data:image/')) {
        return url;
      }
      return '';
    }

    // URL 파싱하여 프로토콜 확인
    const parsed = new URL(url, window.location.origin);
    if (allowedProtocols.includes(parsed.protocol)) {
      // javascript: 프로토콜 차단
      if (parsed.protocol === 'javascript:') {
        return '';
      }
      return url;
    }
  } catch (e) {
    // 상대 경로인 경우 허용
    if (url.startsWith('/') && !url.startsWith('//')) {
      return url;
    }
  }
  return '';
};

// XSS 방지: 텍스트 이스케이프
const escapeHtml = (text) => {
  if (!text || typeof text !== 'string') return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

function Header({ toggleSidebar, isFullscreen, toggleFullscreen }) {
  const navigate = useNavigate();
  const { settings } = useSystemSettings();
  const [showAlarms, setShowAlarms] = useState(false);
  const [alarmData, setAlarmData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasNewAlarm, setHasNewAlarm] = useState(false);
  const prevAlarmIdsRef = useRef(new Set());

  // 공지사항 상태
  const [notices, setNotices] = useState([]);
  const [currentNoticeIndex, setCurrentNoticeIndex] = useState(0);
  const [showNoticeDropdown, setShowNoticeDropdown] = useState(false);

  // 알람 데이터 로드 함수
  const loadAlarms = async () => {
    try {
      setLoading(true);
      const data = await fetchRecentAlarms(20);

      // 신규 알람 체크
      if (data?.recentAlarms?.length > 0) {
        const currentAlarmIds = new Set(data.recentAlarms.map(a => a.alarmId));
        const hasNew = data.recentAlarms.some(a => !prevAlarmIdsRef.current.has(a.alarmId));

        // 첫 로드가 아니고 신규 알람이 있으면 번쩍임 활성화
        if (prevAlarmIdsRef.current.size > 0 && hasNew) {
          setHasNewAlarm(true);
        }

        prevAlarmIdsRef.current = currentAlarmIds;
      }

      setAlarmData(data);
    } catch (err) {
      console.error('Failed to load alarms:', err);
      // 에러 시 빈 데이터
      setAlarmData({
        totalCount: 0,
        errorCount: 0,
        warningCount: 0,
        infoCount: 0,
        recentAlarms: []
      });
    } finally {
      setLoading(false);
    }
  };

  // 초기 알람 로드
  useEffect(() => {
    loadAlarms();
  }, []);

  // 자동 갱신 (일반 interval + ETL 폴링 모드 모두 지원)
  useAutoRefresh(() => {
    loadAlarms();
  });

  // 공지사항 데이터 로드
  useEffect(() => {
    const loadNotices = async () => {
      try {
        const data = await fetchNotices();
        setNotices(data || []);
      } catch (err) {
        console.error('Failed to load notices:', err);
        // 에러 시 샘플 데이터
        setNotices([
          { id: 1, title: '시스템 정기 점검 안내 (12/10 02:00~06:00)', type: 'notice', createdAt: '2024-12-01' },
          { id: 2, title: '신규 AI 분석 기능이 추가되었습니다', type: 'update', createdAt: '2024-11-28' }
        ]);
      }
    };

    loadNotices();
  }, []);

  // 공지사항 자동 롤링 (10초마다)
  useEffect(() => {
    if (notices.length <= 1) return;

    const intervalId = setInterval(() => {
      setCurrentNoticeIndex(prev => (prev + 1) % notices.length);
    }, 10000);

    return () => clearInterval(intervalId);
  }, [notices.length]);

  // 알람 목록 (실제 데이터 또는 빈 배열)
  const alarms = alarmData?.recentAlarms || [];
  const errorCount = alarmData?.errorCount || 0;

  const logout = async () => {
    // 로그아웃 처리 (localStorage 정리)
    await authLogout();
    navigate('/login');
  };

  const handleAlarmClick = (alarm) => {
    // 알람 드롭다운 닫기
    setShowAlarms(false);

    // 사출기 페이지로 이동 (알람 탭 열기)
    navigate('/machine', {
      state: {
        machineData: {
          id: alarm.equipmentId,
          name: alarm.equipmentName
        },
        openTab: 'alarm',
        alarmId: alarm.alarmCode
      }
    });
  };

  return (
    <header className="header">
      <div className="header-left">
        <div className="logo" onClick={() => navigate('/home')} style={{cursor: 'pointer'}}>
          {settings.logoUrl ? (
            <img
              src={sanitizeImageUrl(getFullImageUrl(settings.logoUrl))}
              alt="로고"
              className="logo-image"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            'S'
          )}
        </div>
        <div className="menu-icon" onClick={toggleSidebar} style={{cursor: 'pointer'}}>
          <span className="material-symbols-outlined" style={{color: '#CAD2E0', fontSize: '24px'}}>
            menu
          </span>
        </div>
        <div className="title">사출 통합 관제 시스템</div>
        <div className="fullscreen-btn" onClick={toggleFullscreen} title={isFullscreen ? '전체화면 종료' : '전체화면'}>
          <span className="material-symbols-outlined" style={{color: '#CAD2E0', fontSize: '24px'}}>
            {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
          </span>
        </div>
      </div>

      {/* 공지사항 (중앙) */}
      <div className="header-center">
        <div className="notice-container">
          {notices.length > 0 ? (
            <>
              <div
                className="notice-display-box"
                onClick={() => setShowNoticeDropdown(!showNoticeDropdown)}
              >
                <span className="material-symbols-outlined notice-icon">
                  {notices[currentNoticeIndex]?.type === 'urgent' ? 'priority_high' : 'campaign'}
                </span>
                <span className="notice-display-text">
                  {notices[currentNoticeIndex]?.title}
                </span>
                {notices.length > 1 && (
                  <span className="notice-counter">{currentNoticeIndex + 1}/{notices.length}</span>
                )}
              </div>

              {showNoticeDropdown && (
                <div className="notice-dropdown">
                  <div className="notice-dropdown-header">
                    <h3>공지사항</h3>
                    <span className="notice-count">{notices.length}건</span>
                  </div>
                  <div className="notice-list">
                    {notices.map((notice, index) => (
                      <div
                        key={notice.id}
                        className={`notice-item ${notice.type === 'urgent' ? 'notice-urgent' : ''}`}
                        onClick={() => {
                          setCurrentNoticeIndex(index);
                          setShowNoticeDropdown(false);
                          navigate('/notices', { state: { openNoticeId: notice.id } });
                        }}
                      >
                        <div className="notice-item-row">
                          <span className={`notice-type-badge ${notice.type}`}>
                            {notice.type === 'urgent' ? '긴급' : notice.type === 'update' ? '업데이트' : '공지'}
                          </span>
                          <span className="notice-category-badge">{notice.category || '일반'}</span>
                          <span className="notice-title">{notice.title}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="notice-display-box empty">
              <span className="material-symbols-outlined notice-icon">campaign</span>
              <span className="notice-display-text">공지사항이 없습니다</span>
            </div>
          )}
        </div>
      </div>

      <div className="header-right">
        {/* 웹 상단 알림이 활성화된 경우에만 알람 표시 */}
        {settings.notifyWeb !== false && (
          <div className="alarm-container">
            {loading ? (
              <div className="alarm-display-box">
                <span className="alarm-display-text">알람 로딩 중...</span>
              </div>
            ) : alarms.length > 0 ? (
              <>
                <div className={`alarm-display-box ${hasNewAlarm ? 'has-alarm' : ''}`} onClick={() => { setShowAlarms(!showAlarms); setHasNewAlarm(false); }}>
                  <span className="material-symbols-outlined alarm-icon-inline">
                    {alarms[0].severity === 'ERROR' ? 'error' : alarms[0].severity === 'WARNING' ? 'warning' : 'info'}
                  </span>
                  <span className="alarm-display-text">
                    {alarms[0].factoryName} {alarms[0].equipmentName} {alarms[0].message}
                  </span>
                </div>

                {showAlarms && (
                  <div className="alarm-dropdown">
                    <div className="alarm-dropdown-header">
                      <h3>알람 현황</h3>
                      <span className="alarm-count">{alarmData.totalCount}건</span>
                    </div>
                    <div className="alarm-list">
                      {alarms.map(alarm => (
                        <div
                          key={alarm.alarmId}
                          className={`alarm-item alarm-${alarm.severity.toLowerCase()}`}
                          onClick={() => handleAlarmClick(alarm)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="alarm-item-header">
                            <span className="material-symbols-outlined alarm-icon-small">
                              {alarm.severity === 'ERROR' ? 'error' : alarm.severity === 'WARNING' ? 'warning' : 'info'}
                            </span>
                            <span className="alarm-factory">{alarm.factoryName}</span>
                            <span className="alarm-machine">{alarm.equipmentName}</span>
                          </div>
                          <div className="alarm-message">{alarm.message}</div>
                          <div className="alarm-time">{alarm.timeAgo}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="alarm-display-box">
                <span className="material-symbols-outlined alarm-icon-inline">check_circle</span>
                <span className="alarm-display-text">알람 없음</span>
              </div>
            )}
          </div>
        )}

        <div className="header-icon" title="홈" onClick={() => navigate('/')}>
          <span className="material-symbols-outlined" style={{color: '#CAD2E0', fontSize: '24px'}}>
            home
          </span>
        </div>
        <div className="header-icon" title="계정" onClick={() => navigate('/mypage')}>
          <span className="material-symbols-outlined" style={{color: '#CAD2E0', fontSize: '24px'}}>
            account_circle
          </span>
        </div>
        <div className="header-icon" title="로그아웃">
          <span className="material-symbols-outlined" style={{color: '#CAD2E0', fontSize: '24px'}}
          onClick={logout}>
            logout
          </span>
        </div>
      </div>
    </header>
  );
}

export default Header;
